import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Pattern, usePatternContext } from '../contexts/PatternContext'
import {
  COMPOSITE_GATE_MESSAGE,
  compositeGateActive,
  patternHasComposites,
} from '../../../common/utils/composite'
import { PatternCopilotProvider, PatternCopilotTab } from '../copilot'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import {
  ConditionGroupType,
  PatternTreeContext,
  PatternTreeProvider,
} from '../contexts/PatternTreeContext'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  List,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { alpha, styled, useTheme } from '@mui/material/styles'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import EditIcon from '@mui/icons-material/Edit'
import { ContentCopy, Save } from '@mui/icons-material'
import DeleteIcon from '@mui/icons-material/Delete'
import ReplyIcon from '@mui/icons-material/Reply'
import FileCopyIcon from '@mui/icons-material/FileCopy'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import AssessmentIcon from '@mui/icons-material/Assessment'
import HistoryIcon from '@mui/icons-material/History'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import TuneIcon from '@mui/icons-material/Tune'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import NotificationAddIcon from '@mui/icons-material/NotificationAdd'
import TriggerChartSelector from './TriggerChartSelector'
import { ConditionGroupListItem, HighlightedPrereqContext } from './PatternListItem'
import { PatternPreviewPanel, PreviewConflictProvider } from '../preview'
import { getConditionTypes } from '../condition/conditions'
import { useApplicationSuiteContext } from '../../../contexts/ApplicationSuiteProvider'
import { useSavePattern } from '../../../hooks/useSavePattern'
import { useCopyPatternWithSave } from '../../../hooks/useCopyPatternToAccount'
import { SnackbarHelperContext } from '../../../contexts/SnackbarHelperContext'
import ShareDialog from './sidebar/patternInput/ShareDialog'
import generateShareLink from '../../../common/utils/generateShareLink'
import ReactGA from 'react-ga4'
import _cloneDeep from 'lodash.clonedeep'
import { deletePattern } from '../../../common/utils/deletePattern'
import AlertsTab from './tabs/AlertsTab'
import BacktestsTab from './tabs/BacktestsTab'
import VersionTab from './tabs/VersionTab'
import { usePatternSidePanelContext } from '../contexts/PatternSidePanelContext'
import { useModalContext } from '../../../contexts/ModalProvider'
import { defaultInputSettings } from '../../../contexts/ApplicationSuiteProvider'
import { useBacktestContext } from '../../backtest/contexts/BacktestContext'
import CreateAlertActionButton from '../../../layout/settingsMenu/CreateAlertActionButton'
import BacktestPanelContainer from '../../../layout/BacktestPanelContainer'
import LockIcon from '@mui/icons-material/Lock'
import UpgradeDialog from '../../../components/UpgradeDialog'
import { useDrop } from 'react-dnd'

// Styled components
const DetailContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.custom1,
  color: theme.palette.text.primary,
}))

const MainEditorArea = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  height: '100%',
  overflow: 'hidden',
  minWidth: 0,
}))

const EditorHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1.5, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.custom2,
  gap: theme.spacing(1.5),
  flexShrink: 0,
}))

const ControlsBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1.5, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.custom1,
  gap: theme.spacing(2),
  flexWrap: 'wrap',
  flexShrink: 0,
}))

const ScrollableContent = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  padding: theme.spacing(2),
}))

const SidePanelContainer = styled(Box)(({ theme }) => ({
  width: 380,
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  borderLeft: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.custom2,
  color: theme.palette.text.primary,
  overflow: 'hidden',
}))

const SidePanelActionBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  padding: theme.spacing(1.5, 2),
  backgroundColor: theme.palette.background.custom4,
  borderTop: `1px solid rgba(29, 195, 14, 0.15)`,
  boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.2)',
  flexShrink: 0,
}))

interface PatternDetailViewProps {
  pattern: Pattern
  onBack: () => void
  isTradingHousePattern?: boolean
}

function PatternDetailView({ pattern, onBack, isTradingHousePattern }: PatternDetailViewProps) {
  return (
    <PatternTreeProvider pattern={pattern}>
      <PatternCopilotProvider>
        <PatternDetailViewInner
          pattern={pattern}
          onBack={onBack}
          isTradingHousePattern={isTradingHousePattern}
        />
      </PatternCopilotProvider>
    </PatternTreeProvider>
  )
}

function PatternDetailViewInner({
  pattern: initialPattern,
  onBack,
  isTradingHousePattern,
}: PatternDetailViewProps) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const { usermeta, tradingHouse, canViewPrivatePatterns, canCreateAlerts } =
    useApplicationSuiteContext()
  const isEditable = !isTradingHousePattern || usermeta.isAdmin
  const { setSnackbarSettings } = useContext(SnackbarHelperContext)
  const { setIsBacktestRunDialogOpen, setIsCreateAlertModalOpen, setDefaultAlert } =
    useModalContext()
  const { setBacktestSettingInputData } = useBacktestContext()
  const { focusedBacktest, setFocusedBacktest, setBacktestToView } = usePatternContext()
  const { setSelectedPattern, setActiveSidePanelTab, activeSidePanelTab } =
    usePatternSidePanelContext()

  const {
    pattern,
    activeTab,
    setActiveTab,
    addConditionGroup,
    handleDrop,
    handleChangePattern,
    savePattern,
    isSavingPattern,
  } = useContext(PatternTreeContext)

  const usesComposites = useMemo(() => patternHasComposites(pattern ?? {}), [pattern])
  const compositeGated = useMemo(() => compositeGateActive(pattern ?? {}), [pattern])

  // AI Builder copilot: admin-only phase 1 (mirrors the chatAssistant 403),
  // excluded for trading-house standard patterns; lives as a side-panel tab
  const copilotEnabled = !isTradingHousePattern && !!usermeta?.isAdmin

  const { forceSave, hasPatternChanged } = useSavePattern(
    () => {},
    usermeta,
    tradingHouse,
    canViewPrivatePatterns
  )
  const { copyPattern } = useCopyPatternWithSave(forceSave)

  const [isEditingName, setIsEditingName] = useState(false)
  const [editedName, setEditedName] = useState(pattern?.name || '')
  const [patternChanged, setPatternChanged] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false)
  const [duplicateName, setDuplicateName] = useState('')
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false)
  const [shareable, setShareable] = useState<any>(null)
  const [isSaveAsStandardDialogOpen, setIsSaveAsStandardDialogOpen] = useState(false)
  const [triggerChartEditing, setTriggerChartEditing] = useState(false)
  const [triggerChartHovering, setTriggerChartHovering] = useState(false)
  const [sidePanelOpen, setSidePanelOpen] = useState(false)
  const [sideTabValue, setSideTabValue] = useState<'alerts' | 'backtests' | 'ai' | 'version'>(
    'alerts'
  )
  const [headerMenuAnchor, setHeaderMenuAnchor] = useState<null | HTMLElement>(null)
  const [isUpgradeDialogOpen, setIsUpgradeDialogOpen] = useState(false)

  const [highlightedIds, setHighlightedIds] = useState<Set<string>>(new Set())

  const currentConditionGroups =
    activeTab === 'buy' ? pattern?.buyConditionGroups || [] : pattern?.sellConditionGroups || []

  // Check if pattern has any invalid conditions (including legacy CHANGED_* missing threshold)
  const hasInvalidConditions = (() => {
    if (!pattern) return false
    const allGroups = [
      ...(pattern.buyConditionGroups || []),
      ...(pattern.sellConditionGroups || []),
    ]
    const check = (groups: ConditionGroupType[]): boolean => {
      for (const group of groups) {
        for (const condition of group.conditions || []) {
          if (!condition.condition.isValid) return true
          const ct = getConditionTypes(condition?.condition?.LHS?.field)?.find(
            (c) => c.value === condition?.condition?.conditionType
          )
          if (ct?.hasThreshold && condition?.condition?.threshold === undefined) return true
        }
        if (group.conditionGroups?.length && check(group.conditionGroups)) return true
      }
      return false
    }
    return check(allGroups)
  })()

  // Sync selectedPattern for side panel sub-components
  useEffect(() => {
    if (pattern) {
      setSelectedPattern(pattern)
    }
    return () => {
      setSelectedPattern(null)
    }
  }, [pattern, setSelectedPattern])

  // Check if pattern has changed
  useEffect(() => {
    let isMounted = true
    const check = async () => {
      if (pattern) {
        const changed = await hasPatternChanged(pattern, isTradingHousePattern)
        if (isMounted) setPatternChanged(changed)
      }
    }
    check()
    return () => {
      isMounted = false
    }
  }, [hasPatternChanged, pattern, isTradingHousePattern])

  // Sync tab value
  useEffect(() => {
    if (
      activeSidePanelTab === 'alerts' ||
      activeSidePanelTab === 'backtests' ||
      activeSidePanelTab === 'version'
    ) {
      setSideTabValue(activeSidePanelTab)
    }
  }, [activeSidePanelTab])

  // Root level drop functionality
  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: ['group'],
      canDrop: (item: any) => isEditable && item.patternId === pattern?.id,
      drop: (item: any, monitor) => {
        if (!isEditable || monitor.didDrop() || item.patternId !== pattern?.id) return
        if (item.type === 'group' && item.sourceGroupId) {
          handleDrop('ROOT_GROUP', item, 0)
        }
      },
      collect: (monitor) => ({
        isOver:
          !!monitor.isOver({ shallow: true }) &&
          monitor.getItem()?.patternId === pattern?.id &&
          isEditable,
        canDrop: !!monitor.canDrop() && monitor.getItem()?.patternId === pattern?.id && isEditable,
      }),
    }),
    [handleDrop, pattern?.id, isEditable]
  )

  const handleNameSave = () => {
    if (!pattern) return
    handleChangePattern('name', editedName)
    setIsEditingName(false)
  }

  const handleNameKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleNameSave()
    else if (e.key === 'Escape') {
      setEditedName(pattern?.name || '')
      setIsEditingName(false)
    }
  }

  const handleRunBacktest = () => {
    if (!pattern) return
    if (compositeGated) {
      setHeaderMenuAnchor(null)
      setSnackbarSettings({ severity: 'info', message: COMPOSITE_GATE_MESSAGE })
      return
    }
    setHeaderMenuAnchor(null)
    setBacktestSettingInputData({
      ...defaultInputSettings,
      patternId: pattern.id,
    })
    setIsBacktestRunDialogOpen(true)
  }

  const handleCreateAlert = () => {
    if (!pattern) return
    if (compositeGated) {
      setHeaderMenuAnchor(null)
      setSnackbarSettings({ severity: 'info', message: COMPOSITE_GATE_MESSAGE })
      return
    }
    setHeaderMenuAnchor(null)
    const _pattern = _cloneDeep(pattern)
    setDefaultAlert((prev: any) => ({
      ...prev,
      pattern: _pattern,
      name: `${_pattern?.name || 'Unnamed Pattern'} - Alert`,
      from: 'pattern',
    }))
    setIsCreateAlertModalOpen(true)
  }

  const handleShare = async () => {
    if (!pattern?.id) return
    try {
      const link = await generateShareLink(
        pattern.id,
        `usermeta/${usermeta.gcid}/patterns/${pattern.id}`,
        'PATTERN',
        { name: pattern.name, createdBy: { gcid: usermeta?.gcid, email: usermeta?.email } }
      )
      setShareable(link)
      setIsShareDialogOpen(true)
    } catch (error) {
      console.error('Error generating share link:', error)
    }
  }

  const handleCopyPattern = async () => {
    if (!pattern) return
    await copyPattern(pattern)
  }

  const handleDuplicatePattern = async () => {
    if (!pattern) return
    try {
      setSnackbarSettings({
        message: 'Duplicating pattern...',
        severity: 'info',
        autoHideDuration: 3000,
      })
      const copy = JSON.parse(JSON.stringify(pattern))
      delete copy.id
      copy.name = duplicateName || `${pattern.name} (Copy)`
      copy.version = undefined
      copy.alertVersion = undefined
      const saved = await forceSave(copy)
      if (saved) {
        ReactGA.event('pattern_duplicate', { pattern_name: copy.name })
        setSnackbarSettings({
          message: 'Pattern duplicated!',
          severity: 'success',
          autoHideDuration: 3000,
        })
      }
    } catch (error) {
      console.error('Error duplicating pattern:', error)
      setSnackbarSettings({
        message: 'Error duplicating pattern',
        severity: 'error',
        autoHideDuration: 6000,
      })
    }
    setIsDuplicateDialogOpen(false)
  }

  const handleDeletePattern = async () => {
    if (!pattern) return
    try {
      await deletePattern(pattern, isTradingHousePattern, usermeta)
      setSnackbarSettings({
        message: 'Pattern deleted!',
        severity: 'success',
        autoHideDuration: 3000,
      })
      onBack()
    } catch (error) {
      console.error('Error deleting pattern:', error)
      setSnackbarSettings({
        message: 'Error deleting pattern',
        severity: 'error',
        autoHideDuration: 6000,
      })
    }
    setDeleteDialogOpen(false)
  }

  const saveAsStandardPattern = async () => {
    if (!usermeta.isAdmin || !pattern) return
    // Standard patterns fan out to other users via alert templates; composites
    // are per-user and the backends can't evaluate them yet.
    if (usesComposites) {
      setSnackbarSettings({ severity: 'info', message: COMPOSITE_GATE_MESSAGE })
      return
    }
    try {
      const copy = { ...pattern }
      delete copy.id
      await forceSave(copy, true)
      setSnackbarSettings({
        message: 'Saved as standard pattern!',
        severity: 'success',
        autoHideDuration: 3000,
      })
    } catch (error) {
      console.error('Error saving pattern:', error)
      setSnackbarSettings({
        message: 'Error saving pattern',
        severity: 'error',
        autoHideDuration: 6000,
      })
    }
  }

  const handleSideTabChange = (
    _: React.SyntheticEvent,
    newValue: 'alerts' | 'backtests' | 'ai' | 'version'
  ) => {
    setSideTabValue(newValue)
    // The AI Builder tab has no side-panel-context mapping
    if (newValue !== 'ai') setActiveSidePanelTab(newValue)
  }

  if (!pattern) return null

  // Side panel content (shared between desktop inline and mobile drawer)
  const sidePanelContent = (
    <>
      {/* Side Panel Tabs */}
      <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Tabs
          value={sideTabValue}
          onChange={handleSideTabChange}
          variant="fullWidth"
          textColor="primary"
          indicatorColor="primary"
          sx={{
            '& .MuiTab-root': {
              minHeight: 44,
              textTransform: 'none',
              fontSize: '0.8rem',
              fontWeight: 600,
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
          }}
        >
          <Tab
            value="alerts"
            label="Alerts"
            icon={<NotificationsActiveIcon />}
            iconPosition="start"
            data-tour-id="pattern-alerts-tab"
          />
          <Tab
            value="backtests"
            label="Backtests"
            icon={<AssessmentIcon />}
            iconPosition="start"
            data-tour-id="pattern-backtests-tab"
          />
          {copilotEnabled && (
            <Tab
              value="ai"
              label="AI"
              icon={<SmartToyOutlinedIcon />}
              iconPosition="start"
              data-tour-id="pattern-ai-tab"
            />
          )}
          <Tab
            value="version"
            label="Versions"
            icon={<HistoryIcon />}
            iconPosition="start"
            data-tour-id="pattern-version-tab"
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {sideTabValue === 'alerts' && <AlertsTab />}
        {sideTabValue === 'backtests' && <BacktestsTab />}
        {sideTabValue === 'version' && <VersionTab />}
        {/* AI Builder stays MOUNTED across tab switches (display toggle) so
            the conversation isn't discarded; keyed by the prop pattern's id */}
        {copilotEnabled && (
          <Box
            sx={{
              display: sideTabValue === 'ai' ? 'flex' : 'none',
              flexDirection: 'column',
              height: '100%',
              overflow: 'hidden',
            }}
          >
            <PatternCopilotTab key={initialPattern.id ?? 'unsaved'} />
          </Box>
        )}
      </Box>

      {/* Action Bar */}
      {isEditable && (
        <SidePanelActionBar data-tour-id="pattern-actions-section">
          {compositeGated ? (
            <Tooltip title={COMPOSITE_GATE_MESSAGE}>
              <span>
                <Button variant="outlined" disabled endIcon={<NotificationAddIcon />}>
                  Create Alert
                </Button>
              </span>
            </Tooltip>
          ) : (
            <CreateAlertActionButton pattern={pattern} asButton />
          )}
          <Tooltip title={compositeGated ? COMPOSITE_GATE_MESSAGE : ''}>
            <span>
              <Button
                variant="contained"
                onClick={handleRunBacktest}
                disabled={!pattern || compositeGated}
                endIcon={<PlayArrowIcon />}
              >
                Backtest
              </Button>
            </span>
          </Tooltip>
        </SidePanelActionBar>
      )}
    </>
  )

  return (
    <DetailContainer>
      <PreviewConflictProvider>
        <MainEditorArea>
          {/* Header */}
          <EditorHeader>
            <Tooltip title="Back to patterns">
              <IconButton
                onClick={() => {
                  // Publish is the only save — leaving with unsaved changes loses them
                  if (
                    patternChanged &&
                    !window.confirm('Discard unsaved changes to this pattern?')
                  ) {
                    return
                  }
                  onBack()
                }}
                size="small"
              >
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>

            {/* Pattern Name */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1, minWidth: 0 }}>
              {isEditingName ? (
                <input
                  autoFocus
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  onBlur={handleNameSave}
                  onKeyDown={handleNameKeyPress}
                  style={{
                    border: 'none',
                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                    background: 'transparent',
                    color: theme.palette.text.primary,
                    fontSize: '1.1rem',
                    padding: '4px 8px',
                    width: '300px',
                    maxWidth: '100%',
                    outline: 'none',
                    fontFamily: theme.typography.fontFamily,
                    fontWeight: 600,
                  }}
                />
              ) : (
                <>
                  <Typography
                    variant="h6"
                    color="text.primary"
                    data-tour-id="pattern-name"
                    sx={{
                      fontWeight: 700,
                      fontSize: '1.1rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {pattern.name || 'Unnamed Pattern'}
                  </Typography>
                  {isEditable && (
                    <Tooltip title="Edit name">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditedName(pattern.name || '')
                          setIsEditingName(true)
                        }}
                        sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              )}

              {isTradingHousePattern && (
                <Chip
                  label="Standard Pattern"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', height: 24 }}
                />
              )}

              {usesComposites && (
                <Tooltip title={COMPOSITE_GATE_MESSAGE}>
                  <Chip
                    label="Custom indicators"
                    size="small"
                    color="info"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 24 }}
                  />
                </Tooltip>
              )}
            </Box>

            {/* Header Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              {isTradingHousePattern ? (
                <>
                  {isEditable && (
                    <Tooltip
                      title={
                        hasInvalidConditions
                          ? 'Fix invalid conditions before publishing'
                          : !patternChanged && !isSavingPattern
                            ? 'No unsaved changes'
                            : ''
                      }
                    >
                      <span>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={() => savePattern(true)}
                          disabled={isSavingPattern || hasInvalidConditions || !patternChanged}
                          data-tour-id="save-pattern-button"
                        >
                          {`Publish v${(pattern?.version ?? 0) + 1}`}
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                  <Button
                    onClick={handleCopyPattern}
                    size="small"
                    variant="outlined"
                    color="primary"
                    endIcon={<FileCopyIcon fontSize="small" />}
                    sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                    data-tour-id="pattern-copy-button"
                  >
                    Copy to Account
                  </Button>
                  {usermeta.isAdmin && (
                    <>
                      <IconButton
                        size="small"
                        onClick={(e) => setHeaderMenuAnchor(e.currentTarget)}
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                      <Menu
                        anchorEl={headerMenuAnchor}
                        open={Boolean(headerMenuAnchor)}
                        onClose={() => setHeaderMenuAnchor(null)}
                      >
                        <MenuItem
                          onClick={() => {
                            setHeaderMenuAnchor(null)
                            setDeleteDialogOpen(true)
                          }}
                          sx={{ color: theme.palette.error.main }}
                        >
                          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                          Delete
                        </MenuItem>
                      </Menu>
                    </>
                  )}
                </>
              ) : (
                <>
                  <Tooltip
                    title={
                      hasInvalidConditions
                        ? 'Fix invalid conditions before publishing'
                        : !patternChanged && !isSavingPattern
                          ? 'No unsaved changes'
                          : ''
                    }
                  >
                    <span>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => savePattern()}
                        disabled={isSavingPattern || hasInvalidConditions || !patternChanged}
                        data-tour-id="save-pattern-button"
                      >
                        {`Publish v${(pattern?.version ?? 0) + 1}`}
                      </Button>
                    </span>
                  </Tooltip>
                  <IconButton
                    size="small"
                    onClick={(e) => setHeaderMenuAnchor(e.currentTarget)}
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                  <Menu
                    anchorEl={headerMenuAnchor}
                    open={Boolean(headerMenuAnchor)}
                    onClose={() => setHeaderMenuAnchor(null)}
                  >
                    <MenuItem
                      disabled={compositeGated}
                      onClick={
                        canCreateAlerts
                          ? handleCreateAlert
                          : () => {
                              setHeaderMenuAnchor(null)
                              setIsUpgradeDialogOpen(true)
                            }
                      }
                    >
                      {canCreateAlerts ? (
                        <NotificationAddIcon fontSize="small" sx={{ mr: 1 }} />
                      ) : (
                        <LockIcon fontSize="small" sx={{ mr: 1, color: 'warning.main' }} />
                      )}
                      Create Alert
                    </MenuItem>
                    <MenuItem disabled={compositeGated} onClick={handleRunBacktest}>
                      <PlayArrowIcon fontSize="small" sx={{ mr: 1 }} />
                      Run Backtest
                    </MenuItem>
                    <Divider />
                    <MenuItem
                      onClick={() => {
                        setHeaderMenuAnchor(null)
                        setDuplicateName(`${pattern?.name || 'Pattern'} (Copy)`)
                        setIsDuplicateDialogOpen(true)
                      }}
                    >
                      <ContentCopy fontSize="small" sx={{ mr: 1 }} />
                      Duplicate
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setHeaderMenuAnchor(null)
                        handleShare()
                      }}
                    >
                      <ReplyIcon sx={{ transform: 'scaleX(-1)', mr: 1 }} fontSize="small" />
                      Share
                    </MenuItem>
                    {usermeta.isAdmin && (
                      <MenuItem
                        onClick={() => {
                          setHeaderMenuAnchor(null)
                          setIsSaveAsStandardDialogOpen(true)
                        }}
                      >
                        <AdminPanelSettingsIcon fontSize="small" sx={{ mr: 1 }} color="warning" />
                        Save as Standard
                      </MenuItem>
                    )}
                    <Divider />
                    <MenuItem
                      onClick={() => {
                        setHeaderMenuAnchor(null)
                        setDeleteDialogOpen(true)
                      }}
                      sx={{ color: theme.palette.error.main }}
                    >
                      <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                      Delete
                    </MenuItem>
                  </Menu>
                </>
              )}

              {/* Mobile: toggle side panel — only for user's own patterns */}
              {isMobile && isEditable && (
                <Tooltip title="Details Panel">
                  <IconButton size="small" onClick={() => setSidePanelOpen(true)}>
                    <TuneIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </EditorHeader>

          {/* Controls Bar */}
          <ControlsBar>
            <ToggleButtonGroup
              color="primary"
              value={activeTab}
              exclusive
              onChange={(_, newTab) => newTab && setActiveTab(newTab)}
              size="small"
              data-tour-id="pattern-buy-sell-tab"
            >
              <ToggleButton value="buy" sx={{ height: 36, width: 65 }}>
                BUY
              </ToggleButton>
              <ToggleButton value="sell" sx={{ height: 36, width: 65 }}>
                SELL
              </ToggleButton>
            </ToggleButtonGroup>

            <Tooltip
              title="Set the chart periodicity and timeframe that triggers this pattern."
              open={triggerChartHovering && !triggerChartEditing}
            >
              <Box
                onMouseEnter={() => setTriggerChartHovering(true)}
                onMouseLeave={() => setTriggerChartHovering(false)}
              >
                <TriggerChartSelector readOnly={!isEditable} onEdit={setTriggerChartEditing} />
              </Box>
            </Tooltip>
          </ControlsBar>

          {/* Live preview of what the pattern's conditions describe */}
          <PatternPreviewPanel />

          {/* Scrollable condition groups editor */}
          <ScrollableContent data-tour-id="pattern-item">
            <Box
              ref={isEditable ? drop : null}
              sx={{
                backgroundColor:
                  isOver && canDrop ? alpha(theme.palette.primary.main, 0.1) : 'inherit',
                border: isOver && canDrop ? `2px dashed ${theme.palette.primary.main}` : 'none',
                borderRadius: 1,
                transition: 'background-color 0.2s, border 0.2s',
                padding: isOver && canDrop ? 2 : 0,
                minHeight: 200,
              }}
            >
              <HighlightedPrereqContext.Provider value={{ highlightedIds, setHighlightedIds }}>
                {currentConditionGroups.length > 0 ? (
                  <List sx={{ width: '100%', padding: 0 }}>
                    {currentConditionGroups.map((group: ConditionGroupType) => (
                      <ConditionGroupListItem
                        key={group.id}
                        group={group}
                        isReadOnly={!isEditable}
                      />
                    ))}
                  </List>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 6,
                      gap: 2,
                    }}
                  >
                    <AccountTreeIcon sx={{ fontSize: 48, opacity: 0.15 }} />
                    <Typography variant="body2" color="text.secondary">
                      No {activeTab.toUpperCase()} condition groups defined.
                    </Typography>
                    {isEditable && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => addConditionGroup()}
                        sx={{ textTransform: 'none' }}
                      >
                        Add your first group
                      </Button>
                    )}
                  </Box>
                )}
              </HighlightedPrereqContext.Provider>

              {isEditable && currentConditionGroups.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 1 }}>
                  <Button
                    onClick={() => addConditionGroup()}
                    variant="text"
                    sx={{ textTransform: 'none', fontSize: '0.9rem', px: 2 }}
                  >
                    New Group +
                  </Button>
                </Box>
              )}
            </Box>
          </ScrollableContent>
        </MainEditorArea>
      </PreviewConflictProvider>

      {/* Desktop Side Panel — only for user's own patterns */}
      {!isMobile && isEditable && (
        <SidePanelContainer data-tour-id="pattern-side-panel">
          {focusedBacktest ? (
            <BacktestPanelContainer
              backtest={focusedBacktest}
              onClose={() => setFocusedBacktest(null)}
              onView={() => setBacktestToView(focusedBacktest)}
            />
          ) : (
            sidePanelContent
          )}
        </SidePanelContainer>
      )}

      {/* Mobile Side Panel Drawer — only for user's own patterns */}
      {isMobile && isEditable && (
        <Drawer
          anchor="right"
          open={sidePanelOpen}
          onClose={() => setSidePanelOpen(false)}
          PaperProps={{
            sx: {
              width: 380,
              maxWidth: '100%',
              height: '100%',
              backgroundColor: theme.palette.background.custom2,
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          {focusedBacktest ? (
            <BacktestPanelContainer
              backtest={focusedBacktest}
              onClose={() => setFocusedBacktest(null)}
              onView={() => setBacktestToView(focusedBacktest)}
            />
          ) : (
            sidePanelContent
          )}
        </Drawer>
      )}

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Pattern Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this pattern? All alerts and backtests associated with
            this pattern will still be available.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary" variant="contained">
            Cancel
          </Button>
          <Button onClick={handleDeletePattern} color="error" autoFocus variant="outlined">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate Dialog */}
      <Dialog open={isDuplicateDialogOpen} onClose={() => setIsDuplicateDialogOpen(false)}>
        <DialogTitle>Duplicate Pattern</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Create a copy with a new name. The duplicate starts at version 1 with a clean history.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="Pattern Name"
            value={duplicateName}
            onChange={(e) => setDuplicateName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && duplicateName.trim()) handleDuplicatePattern()
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsDuplicateDialogOpen(false)}
            color="primary"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDuplicatePattern}
            color="primary"
            variant="contained"
            disabled={!duplicateName.trim()}
            endIcon={<ContentCopy fontSize="small" />}
          >
            Duplicate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save as Standard Dialog */}
      <Dialog
        open={isSaveAsStandardDialogOpen && usermeta.isAdmin}
        onClose={() => setIsSaveAsStandardDialogOpen(false)}
      >
        <DialogTitle>Save as Standard Pattern</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Save this pattern as a standard pattern? This will make it available to all traders.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsSaveAsStandardDialogOpen(false)}
            color="primary"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              saveAsStandardPattern()
              setIsSaveAsStandardDialogOpen(false)
            }}
            color="primary"
            variant="contained"
            endIcon={isSavingPattern ? <CircularProgress size={20} /> : <Save fontSize="small" />}
          >
            Save as Standard
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Dialog */}
      <ShareDialog
        open={isShareDialogOpen}
        title="Share this pattern"
        onClose={() => setIsShareDialogOpen(false)}
        shareable={shareable}
      />

      {/* Upgrade Dialog */}
      <UpgradeDialog open={isUpgradeDialogOpen} onClose={() => setIsUpgradeDialogOpen(false)} />
    </DetailContainer>
  )
}

export default PatternDetailView
